/**
 * Sends unexpected browser errors to the API (`POST /v1/client-errors`), which logs them and
 * forwards them to error tracking. Only the error name, message, stack and page path are sent;
 * at most a few reports per page load.
 */
export function installErrorReporting(options: {
  apiUrl: string;
  app: 'kitchen' | 'doors' | 'admin' | 'poc';
  release?: string;
  maxReports?: number;
}): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const endpoint = `${options.apiUrl.replace(/\/$/, '')}/v1/client-errors`;
  const seen = new Set<string>();
  let remaining = options.maxReports ?? 5;

  const send = (error: unknown) => {
    const info =
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : { name: 'Error', message: String(error) };
    const key = `${info.name}:${info.message}`;
    if (remaining <= 0 || seen.has(key)) return;
    seen.add(key);
    remaining--;
    const body = JSON.stringify({
      app: options.app,
      name: info.name.slice(0, 100),
      message: info.message.slice(0, 1000),
      stack: info.stack?.slice(0, 8000),
      // path only: query strings may contain configuration or order tokens
      url: location.pathname.slice(0, 500),
      release: options.release,
    });
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
    if (!navigator.sendBeacon?.(endpoint, blob)) {
      void fetch(endpoint, { method: 'POST', body, keepalive: true }).catch(() => undefined);
    }
  };

  const onError = (event: ErrorEvent) => {
    // cross-origin script errors carry no details
    if (event.message === 'Script error.' && !event.error) return;
    send(event.error ?? event.message);
  };
  const onRejection = (event: PromiseRejectionEvent) => send(event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
